import { Container, Graphics, Text } from 'pixi.js';
import type { Scene } from '../core/Scene';
import type { InputSnapshot } from '../input/actions';
import type { GameContext } from '../GameContext';
import { Palette } from '../render/Palette';
import { RunScene } from './RunScene';
import { MenuScene } from './MenuScene';
import { ARTIFACTS_BY_ID } from '../sim/artifacts/ArtifactDefs';
import { tierLabel } from '../sim/run/tiers';
import type { RunEndSummary } from '../sim/meta/runEnd';
import { fitScene } from '../ui/fitLayout';

export interface RunResult {
  win: boolean;
  floor: number;
  /** Ladder tier the run was played at (0 = base game). */
  tier: number;
  seed: string;
  gemsEarned: number;
  timeSeconds: number;
  summary: RunEndSummary;
}

/** M:SS from seconds — shared by EndScene and RecordsScene. */
export function formatRunTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60).toString().padStart(2, '0');
  return `${mins}:${secs}`;
}

function artifactName(id: string): string {
  return ARTIFACTS_BY_ID.get(id)?.name ?? id;
}

function pct(q: number): string {
  return `${Math.round(q * 100)}%`;
}

/** Win/death screen: retry seed / new seed / main menu (Cancel = new seed). */
export class EndScene implements Scene {
  readonly root = new Container();
  readonly blocksUpdateBelow = true;
  private acceptDelay = 30; // ignore inputs briefly so a dodge press can't skip

  private static readonly OPTIONS = ['Retry Seed', 'New Seed', 'Main Menu'] as const;
  private focus = 0;
  private optionTexts: Text[] = [];

  constructor(private readonly ctx: GameContext, private readonly result: RunResult) {
    // Settlement lines first, data only — the fit scale depends on the count.
    const s = result.summary;
    const metaLines: { text: string; color: number }[] = [];
    const banked = [
      ...s.bankedNew.map((b) => `◈ ${artifactName(b.artifactId)} ${pct(b.quality)}`),
      ...s.bankedImproved.map((b) => `◈ ${artifactName(b.artifactId)} ${pct(b.from)}→${pct(b.to)}`),
    ];
    if (banked.length > 0) {
      // On death these survived via descent checkpoints — label accordingly.
      const label = result.win ? 'banked' : 'banked this run';
      metaLines.push({ text: `${label}   ${banked.join(',  ')}`, color: Palette.gem });
    }
    if (s.shardsFromGems > 0) {
      metaLines.push({
        text: `gems tithed   ${result.gemsEarned} → +◇ ${s.shardsFromGems}`,
        color: Palette.playerCool,
      });
    }
    if (s.shardsGained > 0) {
      metaLines.push({
        text: `shards   +◇ ${s.shardsGained}   (balance ◇ ${s.shardBalance})`,
        color: Palette.playerCool,
      });
    }
    if (s.lost.length > 0) {
      metaLines.push({
        text: `lost to the depth   ${s.lost.map((l) => artifactName(l.artifactId)).join(',  ')}`,
        color: Palette.enemyHot,
      });
    }
    for (const m of s.unlocked) {
      metaLines.push({ text: `UNLOCKED   ${m.label}`, color: Palette.gold });
    }
    if (s.newRecord) {
      metaLines.push({
        text: `NEW RECORD   ${result.tier > 0 ? tierLabel(result.tier) : 'the descent'} — ${formatRunTime(result.timeSeconds)}`,
        color: Palette.gold,
      });
    }
    if (s.unlockedTier !== null) {
      metaLines.push({
        text: `UNLOCKED   ${tierLabel(s.unlockedTier)} — the stair goes deeper`,
        color: Palette.gold,
      });
    }

    // Everything is centered on h/2; minH is twice the deeper half-extent
    // (bottom: meta lines + options + hint). Scales down on short viewports.
    const minH = 2 * Math.max(160, 294 + metaLines.length * 24);
    const { w, h } = fitScene(
      this.root, ctx.app.screen.width, ctx.app.screen.height, 900, minH,
    );

    this.root.addChild(new Graphics().rect(0, 0, w, h).fill({ color: 0x05060c, alpha: 0.92 }));

    const title = new Text({
      text: result.win ? 'THE DEPTH YIELDS' : 'YOU DIED',
      style: {
        fontFamily: 'monospace', fontSize: 54, fontWeight: 'bold',
        fill: result.win ? Palette.gold : Palette.enemyHot,
        letterSpacing: 6,
      },
    });
    title.anchor.set(0.5);
    title.position.set(w / 2, h / 2 - 110);
    this.root.addChild(title);

    const lines = [
      ...(result.tier > 0 ? [`difficulty      ${tierLabel(result.tier)}`] : []),
      `floor reached   ${result.floor}`,
      `gems earned     ${result.gemsEarned}`,
      `time            ${formatRunTime(result.timeSeconds)}`,
      ``,
      `seed  ${result.seed}`,
    ];
    const stats = new Text({
      text: lines.join('\n'),
      style: {
        fontFamily: 'monospace', fontSize: 16, fill: Palette.hudText,
        lineHeight: 26, align: 'left',
      },
    });
    stats.anchor.set(0.5, 0);
    stats.position.set(w / 2, h / 2 - 40);
    this.root.addChild(stats);

    // Progression settlement (≤4 lines, comma-joined; built above the fit).
    metaLines.forEach((line, i) => {
      const text = new Text({
        text: line.text,
        style: { fontFamily: 'monospace', fontSize: 14, fill: line.color },
      });
      text.anchor.set(0.5, 0);
      text.position.set(w / 2, h / 2 + 116 + i * 24);
      this.root.addChild(text);
    });

    const optionsY = h / 2 + 116 + metaLines.length * 24 + 40;
    EndScene.OPTIONS.forEach((label, i) => {
      const text = new Text({
        text: label,
        style: { fontFamily: 'monospace', fontSize: 18, fill: Palette.hudText },
      });
      text.anchor.set(0.5);
      text.position.set(w / 2, optionsY + i * 34);
      this.optionTexts.push(text);
      this.root.addChild(text);
    });

    const hint = new Text({
      text: '[↑↓] navigate      [E / Ⓐ] select      [Esc / Ⓑ] new seed',
      style: { fontFamily: 'monospace', fontSize: 13, fill: Palette.hudDim },
    });
    hint.anchor.set(0.5);
    hint.position.set(w / 2, optionsY + EndScene.OPTIONS.length * 34 + 26);
    this.root.addChild(hint);
  }

  enter(): void {}
  exit(): void {}

  update(_dt: number, input: InputSnapshot): void {
    if (this.acceptDelay > 0) {
      this.acceptDelay--;
      return;
    }
    if (input.navY !== 0) {
      const n = this.optionTexts.length;
      this.focus = (this.focus + input.navY + n) % n;
    }
    if (input.confirmPressed) {
      this.select(this.focus);
    } else if (input.cancelPressed) {
      // Muscle-memory shortcut from the two-key era.
      this.ctx.scenes.replace(new RunScene(this.ctx));
    }
  }

  private select(i: number): void {
    if (i === 0) this.ctx.scenes.replace(new RunScene(this.ctx, this.result.seed));
    else if (i === 1) this.ctx.scenes.replace(new RunScene(this.ctx));
    else this.ctx.scenes.replace(new MenuScene(this.ctx));
  }

  render(_alpha: number): void {
    this.optionTexts.forEach((text, i) => {
      text.text = (this.focus === i ? '▸ ' : '  ') + EndScene.OPTIONS[i]!;
      text.style.fill = this.focus === i ? Palette.playerCool : Palette.hudText;
    });
  }
}
